import React, { useState, useEffect, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { DashboardLayout } from '../components/DashboardLayout';
import { getWallets, getCategories, getTransactions, addCashIn, addCashOut, updateTransaction, deleteTransaction } from '../utils/api';
import { toast } from 'sonner';
import { motion } from 'framer-motion';
import { Plus, ArrowUpRight, ArrowDownRight, Edit, Trash2, X, ArrowLeftRight, Check, ChevronsUpDown } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '../components/ui/command';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { formatCurrency } from '../utils/formatCurrency';
import { useNumberFormat } from '../hooks/useNumberFormat';

const CategoryCombobox = ({ categories, value, onChange, testId }) => {
  const [open, setOpen] = useState(false);
  const selectedCategory = categories.find((category) => category.id === value);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          role="combobox"
          aria-expanded={open}
          data-testid={testId}
          className="w-full justify-between bg-stone-50 font-normal"
        >
          <span className="truncate">{selectedCategory ? selectedCategory.name : 'Select category'}</span>
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[--radix-popover-trigger-width] p-0" align="start">
        <Command>
          <CommandInput placeholder="Search category..." />
          <CommandList>
            <CommandEmpty>No category found.</CommandEmpty>
            <CommandGroup>
              {categories.map((category) => (
                <CommandItem
                  key={category.id}
                  value={category.name}
                  onSelect={() => {
                    onChange(category.id);
                    setOpen(false);
                  }}
                >
                  <Check className={`h-4 w-4 ${value === category.id ? 'opacity-100' : 'opacity-0'}`} />
                  <span>{category.name}</span>
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
};

export const Transactions = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [transactions, setTransactions] = useState([]);
  const [wallets, setWallets] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isCashInOpen, setIsCashInOpen] = useState(false);
  const [isCashOutOpen, setIsCashOutOpen] = useState(false);
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [isDetailOpen, setIsDetailOpen] = useState(false);
  const [currentTransaction, setCurrentTransaction] = useState(null);
  const [filterLabel, setFilterLabel] = useState('');
  const [saving, setSaving] = useState({ cashIn: false, cashOut: false, edit: false, delete: false });
  const sortedCategories = useMemo(
    () => [...categories].sort((a, b) => (a.name || '').localeCompare(b.name || '', undefined, { sensitivity: 'base' })),
    [categories]
  );
  
  // Use number formatters
  const cashInAmount = useNumberFormat('');
  const cashOutAmount = useNumberFormat('');
  const editAmount = useNumberFormat('');
  
  const [cashInData, setCashInData] = useState({
    wallet_id: '',
    source: '',
    narration: '',
    date: new Date().toISOString().split('T')[0]  // Default to today
  });
  
  const [cashOutData, setCashOutData] = useState({
    wallet_id: '',
    category_id: '',
    phone_number: '',
    narration: '',
    date: new Date().toISOString().split('T')[0]  // Default to today
  });
  
  const [editData, setEditData] = useState({});
  const [deleteConfirm, setDeleteConfirm] = useState({ show: false, transaction: null, reason: '' });

  useEffect(() => {
    fetchData();
  }, [searchParams]);

  const fetchData = async () => {
    try {
      // Get filter params from URL
      const walletId = searchParams.get('wallet_id');
      const categoryName = searchParams.get('category');
      
      const filters = {};
      if (walletId) filters.wallet_id = walletId;
      if (categoryName) filters.category_name = categoryName;
      
      const [transactionsData, walletsData, categoriesData] = await Promise.all([
        getTransactions(filters),
        getWallets(),
        getCategories()
      ]);
      
      setTransactions(transactionsData);
      setWallets(walletsData);
      setCategories([...categoriesData].sort((a, b) => (a.name || '').localeCompare(b.name || '', undefined, { sensitivity: 'base' })));
      
      // Set filter label
      if (walletId) {
        const wallet = walletsData.find(w => w.id === walletId);
        setFilterLabel(wallet ? `Wallet: ${wallet.name}` : '');
      } else if (categoryName) {
        setFilterLabel(`Category: ${categoryName}`);
      } else {
        setFilterLabel('');
      }
    } catch (error) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const clearFilter = () => {
    setSearchParams({});
    setFilterLabel('');
  };

  const openDetail = (transaction) => {
    setCurrentTransaction(transaction);
    setIsDetailOpen(true);
  };

  const handleCashIn = async (e) => {
    e.preventDefault();
    if (saving.cashIn) return;
    setSaving((state) => ({ ...state, cashIn: true }));
    try {
      await addCashIn(
        cashInData.wallet_id,
        parseFloat(cashInAmount.numericValue),
        cashInData.source,
        cashInData.narration,
        cashInData.date ? new Date(cashInData.date).toISOString() : null
      );
      toast.success('Cash in recorded successfully');
      setIsCashInOpen(false);
      setCashInData({ wallet_id: '', source: '', narration: '', date: new Date().toISOString().split('T')[0] });
      cashInAmount.reset();
      fetchData();
    } catch (error) {
      toast.error(error.message || 'Failed to record cash in');
    } finally {
      setSaving((state) => ({ ...state, cashIn: false }));
    }
  };

  const handleCashOut = async (e) => {
    e.preventDefault();
    if (saving.cashOut) return;
    setSaving((state) => ({ ...state, cashOut: true }));
    try {
      await addCashOut(
        cashOutData.wallet_id,
        cashOutData.category_id,
        parseFloat(cashOutAmount.numericValue),
        cashOutData.phone_number,
        cashOutData.narration,
        cashOutData.date ? new Date(cashOutData.date).toISOString() : null
      );
      toast.success('Cash out recorded successfully');
      setIsCashOutOpen(false);
      setCashOutData({ wallet_id: '', category_id: '', phone_number: '', narration: '', date: new Date().toISOString().split('T')[0] });
      cashOutAmount.reset();
      fetchData();
    } catch (error) {
      toast.error(error.message || 'Failed to record cash out');
    } finally {
      setSaving((state) => ({ ...state, cashOut: false }));
    }
  };
  
  const openEdit = (transaction) => {
    setCurrentTransaction(transaction);
    setEditData({
      wallet_id: transaction.wallet_id,
      source: transaction.source || '',
      category_id: transaction.category_id || '',
      phone_number: transaction.phone_number || '',
      narration: transaction.narration || '',
      date: transaction.date ? new Date(transaction.date).toISOString().split('T')[0] : new Date().toISOString().split('T')[0],
      reason: ''  // For backdated transaction edits
    });
    editAmount.setValue(transaction.amount.toString());
    setIsEditOpen(true);
  };
  
  const handleEdit = async (e) => {
    e.preventDefault();
    if (saving.edit) return;
    setSaving((state) => ({ ...state, edit: true }));
    try {
      // Check if transaction is backdated (date is in the past)
      const transactionDate = new Date(currentTransaction.date);
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const isBackdated = transactionDate < today;
      
      if (isBackdated && !editData.reason?.trim()) {
        toast.error('Reason is required when editing backdated transactions');
        return;
      }
      
      await updateTransaction(currentTransaction.id, {
        ...editData,
        amount: parseFloat(editAmount.numericValue),
        date: editData.date ? new Date(editData.date).toISOString() : null
      });
      toast.success('Transaction updated successfully');
      setIsEditOpen(false);
      setCurrentTransaction(null);
      editAmount.reset();
      fetchData();
    } catch (error) {
      toast.error(error.message || 'Failed to update transaction');
    } finally {
      setSaving((state) => ({ ...state, edit: false }));
    }
  };
  
  const handleDelete = async () => {
    if (!deleteConfirm.reason?.trim()) {
      toast.error('Please provide a reason for deletion');
      return;
    }
    if (saving.delete) return;
    setSaving((state) => ({ ...state, delete: true }));
    try {
      await deleteTransaction(deleteConfirm.transaction.id, deleteConfirm.reason);
      toast.success('Transaction deleted successfully');
      setDeleteConfirm({ show: false, transaction: null, reason: '' });
      fetchData();
    } catch (error) {
      toast.error(error.message || 'Failed to delete transaction');
    } finally {
      setSaving((state) => ({ ...state, delete: false }));
    }
  };

  const openDeleteConfirm = (transaction) => {
    setDeleteConfirm({ show: true, transaction, reason: '' });
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <div className="w-12 h-12 border-4 border-emerald-900 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
            <p className="text-stone-600">Loading transactions...</p>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="max-w-7xl mx-auto space-y-6 sm:space-y-8 pb-20 lg:pb-8">
        {/* Header - Stack on mobile */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="space-y-4"
        >
          <div>
            <h1 className="text-3xl sm:text-4xl font-bold text-stone-900 tracking-tight mb-2">Transactions</h1>
            <p className="text-sm sm:text-base text-stone-600">Track all your cash in and cash out activities</p>
          </div>
          
          {/* Filter Badge */}
          {filterLabel && (
            <div className="flex items-center gap-2">
              <span className="inline-flex items-center gap-3 px-4 py-2 bg-emerald-100 text-emerald-800 rounded-full text-sm font-medium">
                {filterLabel}
                <button 
                  onClick={clearFilter} 
                  className="hover:bg-emerald-200 rounded-full p-1 transition-colors"
                  aria-label="Clear filter"
                >
                  <X className="w-5 h-5" />
                </button>
              </span>
            </div>
          )}
          
          {/* Action Buttons - Full width on mobile */}
          <div className="grid grid-cols-2 gap-2 sm:gap-3">
            <Dialog open={isCashInOpen} onOpenChange={setIsCashInOpen}>
              <DialogTrigger asChild>
                <Button className="bg-emerald-900 hover:bg-emerald-800 text-white rounded-full w-full h-11 sm:h-10 text-sm sm:text-base" data-testid="cash-in-button">
                  <ArrowUpRight className="w-4 h-4 mr-1 sm:mr-2" />
                  Cash In
                </Button>
              </DialogTrigger>
              <DialogContent data-testid="cash-in-dialog">
                <DialogHeader>
                  <DialogTitle>Record Cash In</DialogTitle>
                </DialogHeader>
                <form onSubmit={handleCashIn} className="space-y-4">
                  <div>
                    <Label>Select Wallet</Label>
                    <Select value={cashInData.wallet_id} onValueChange={(value) => setCashInData({ ...cashInData, wallet_id: value })}>
                      <SelectTrigger className="bg-stone-50" data-testid="cash-in-wallet-select">
                        <SelectValue placeholder="Select wallet" />
                      </SelectTrigger>
                      <SelectContent>
                        {wallets.map(w => (
                          <SelectItem key={w.id} value={w.id}>{w.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Amount (TZS)</Label>
                    <Input
                      type="tel"
                      data-testid="cash-in-amount-input"
                      value={cashInAmount.displayValue}
                      onChange={cashInAmount.handleChange}
                      placeholder="0"
                      className="bg-stone-50 text-xl h-12 font-semibold text-center"
                      required
                    />
                    <p className="text-xs text-stone-500 mt-1 text-center">
                      {cashInAmount.numericValue ? `TZS ${cashInAmount.displayValue}` : 'Enter amount'}
                    </p>
                  </div>
                  <div>
                    <Label>Source</Label>
                    <Input
                      data-testid="cash-in-source-input"
                      value={cashInData.source}
                      onChange={(e) => setCashInData({ ...cashInData, source: e.target.value })}
                      placeholder="e.g. Salary, Freelance"
                      className="bg-stone-50"
                      required
                    />
                  </div>
                  <div>
                    <Label>Date</Label>
                    <Input
                      type="date"
                      data-testid="cash-in-date-input"
                      value={cashInData.date}
                      onChange={(e) => setCashInData({ ...cashInData, date: e.target.value })}
                      className="bg-stone-50"
                      max={new Date().toISOString().split('T')[0]}
                    />
                  </div>
                  <div>
                    <Label>Narration (Optional)</Label>
                    <Input
                      data-testid="cash-in-narration-input"
                      value={cashInData.narration}
                      onChange={(e) => setCashInData({ ...cashInData, narration: e.target.value })}
                      placeholder="Additional notes"
                      className="bg-stone-50"
                    />
                  </div>
                  <Button type="submit" disabled={saving.cashIn} className="w-full bg-emerald-900 hover:bg-emerald-800 text-white rounded-full" data-testid="cash-in-submit-button">
                    {saving.cashIn ? 'Saving...' : 'Record Cash In'}
                  </Button>
                </form>
              </DialogContent>
            </Dialog>

            <Dialog open={isCashOutOpen} onOpenChange={setIsCashOutOpen}>
              <DialogTrigger asChild>
                <Button className="bg-orange-800 hover:bg-orange-700 text-white rounded-full w-full h-11 sm:h-10 text-sm sm:text-base" data-testid="cash-out-button">
                  <ArrowDownRight className="w-4 h-4 mr-1 sm:mr-2" />
                  Cash Out
                </Button>
              </DialogTrigger>
              <DialogContent data-testid="cash-out-dialog">
                <DialogHeader>
                  <DialogTitle>Record Cash Out</DialogTitle>
                </DialogHeader>
                <form onSubmit={handleCashOut} className="space-y-4">
                  <div>
                    <Label>Select Wallet</Label>
                    <Select value={cashOutData.wallet_id} onValueChange={(value) => setCashOutData({ ...cashOutData, wallet_id: value })}>
                      <SelectTrigger className="bg-stone-50" data-testid="cash-out-wallet-select">
                        <SelectValue placeholder="Select wallet" />
                      </SelectTrigger>
                      <SelectContent>
                        {wallets.map(w => (
                          <SelectItem key={w.id} value={w.id}>{w.name} ({formatCurrency(w.balance)})</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Category</Label>
                    <CategoryCombobox
                      categories={sortedCategories}
                      value={cashOutData.category_id}
                      onChange={(value) => setCashOutData({ ...cashOutData, category_id: value })}
                      testId="cash-out-category-select"
                    />
                  </div>
                  <div>
                    <Label>Amount (TZS)</Label>
                    <Input
                      type="tel"
                      data-testid="cash-out-amount-input"
                      value={cashOutAmount.displayValue}
                      onChange={cashOutAmount.handleChange}
                      placeholder="0"
                      className="bg-stone-50 text-xl h-12 font-semibold text-center"
                      required
                    />
                    <p className="text-xs text-stone-500 mt-1 text-center">
                      {cashOutAmount.numericValue ? `TZS ${cashOutAmount.displayValue}` : 'Enter amount'}
                    </p>
                  </div>
                  <div>
                    <Label>Phone Number (Optional)</Label>
                    <Input
                      data-testid="cash-out-phone-input"
                      value={cashOutData.phone_number}
                      onChange={(e) => setCashOutData({ ...cashOutData, phone_number: e.target.value })}
                      placeholder="+255..."
                      className="bg-stone-50"
                    />
                  </div>
                  <div>
                    <Label>Date</Label>
                    <Input
                      type="date"
                      data-testid="cash-out-date-input"
                      value={cashOutData.date}
                      onChange={(e) => setCashOutData({ ...cashOutData, date: e.target.value })}
                      className="bg-stone-50"
                      max={new Date().toISOString().split('T')[0]}
                    />
                  </div>
                  <div>
                    <Label>Narration (Optional)</Label>
                    <Input
                      data-testid="cash-out-narration-input"
                      value={cashOutData.narration}
                      onChange={(e) => setCashOutData({ ...cashOutData, narration: e.target.value })}
                      placeholder="Additional notes"
                      className="bg-stone-50"
                    />
                  </div>
                  <Button type="submit" disabled={saving.cashOut} className="w-full bg-orange-800 hover:bg-orange-700 text-white rounded-full" data-testid="cash-out-submit-button">
                    {saving.cashOut ? 'Saving...' : 'Record Cash Out'}
                  </Button>
                </form>
              </DialogContent>
            </Dialog>
          </div>
        </motion.div>

        <Tabs defaultValue="all" className="w-full">
          <TabsList className="mb-6">
            <TabsTrigger value="all" data-testid="filter-all">All Transactions</TabsTrigger>
            <TabsTrigger value="cash_in" data-testid="filter-cash-in">Cash In</TabsTrigger>
            <TabsTrigger value="cash_out" data-testid="filter-cash-out">Cash Out</TabsTrigger>
            <TabsTrigger value="transfer" data-testid="filter-transfer">Transfers</TabsTrigger>
          </TabsList>
          
          <TabsContent value="all">
            <TransactionList transactions={transactions} onEdit={openEdit} onDelete={openDeleteConfirm} onView={openDetail} />
          </TabsContent>
          <TabsContent value="cash_in">
            <TransactionList transactions={transactions.filter(t => t.type === 'cash_in')} onEdit={openEdit} onDelete={openDeleteConfirm} onView={openDetail} />
          </TabsContent>
          <TabsContent value="cash_out">
            <TransactionList transactions={transactions.filter(t => t.type === 'cash_out')} onEdit={openEdit} onDelete={openDeleteConfirm} onView={openDetail} />
          </TabsContent>
          <TabsContent value="transfer">
            <TransactionList transactions={transactions.filter(t => t.type === 'transfer')} onEdit={openEdit} onDelete={openDeleteConfirm} onView={openDetail} />
          </TabsContent>
        </Tabs>
      </div>
      
      {/* Transaction Detail Dialog */}
      <Dialog open={isDetailOpen} onOpenChange={setIsDetailOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Transaction Details</DialogTitle>
          </DialogHeader>
          {currentTransaction && (
            <div className="space-y-4">
              <div className={`text-center py-4 rounded-lg ${
                currentTransaction.type === 'cash_in' ? 'bg-emerald-50' : currentTransaction.type === 'transfer' ? 'bg-stone-100' : 'bg-orange-50'
              }`}>
                <p className={`text-3xl font-bold ${
                  currentTransaction.type === 'cash_in' ? 'text-emerald-900' : currentTransaction.type === 'transfer' ? 'text-stone-900' : 'text-orange-800'
                }`}>
                  {currentTransaction.type === 'cash_in' ? '+' : currentTransaction.type === 'transfer' ? '' : '-'}{formatCurrency(currentTransaction.amount)}
                </p>
                <p className="text-sm text-stone-600 mt-1 capitalize">{currentTransaction.type.replace('_', ' ')}</p>
              </div>
              
              <div className="space-y-3 text-sm">
                <div className="flex justify-between py-2 border-b border-stone-100">
                  <span className="text-stone-500">{currentTransaction.type === 'transfer' ? 'From Wallet' : 'Wallet'}</span>
                  <span className="font-medium text-stone-900">{currentTransaction.wallet_name}</span>
                </div>
                {currentTransaction.type === 'transfer' && currentTransaction.to_wallet_name && (
                  <div className="flex justify-between py-2 border-b border-stone-100">
                    <span className="text-stone-500">To Wallet</span>
                    <span className="font-medium text-stone-900">{currentTransaction.to_wallet_name}</span>
                  </div>
                )}
                {currentTransaction.type === 'cash_in' && currentTransaction.source && (
                  <div className="flex justify-between py-2 border-b border-stone-100">
                    <span className="text-stone-500">Source</span>
                    <span className="font-medium text-stone-900">{currentTransaction.source}</span>
                  </div>
                )}
                {currentTransaction.type === 'cash_out' && currentTransaction.category_name && (
                  <div className="flex justify-between py-2 border-b border-stone-100">
                    <span className="text-stone-500">Category</span>
                    <span className="font-medium text-stone-900">{currentTransaction.category_name}</span>
                  </div>
                )}
                {currentTransaction.phone_number && (
                  <div className="flex justify-between py-2 border-b border-stone-100">
                    <span className="text-stone-500">Phone</span>
                    <span className="font-medium text-stone-900">{currentTransaction.phone_number}</span>
                  </div>
                )}
                {currentTransaction.narration && (
                  <div className="flex justify-between py-2 border-b border-stone-100">
                    <span className="text-stone-500">Narration</span>
                    <span className="font-medium text-stone-900 text-right max-w-[200px]">{currentTransaction.narration}</span>
                  </div>
                )}
                {currentTransaction.transaction_charge > 0 && (
                  <div className="flex justify-between py-2 border-b border-stone-100">
                    <span className="text-stone-500">Transaction Charge</span>
                    <span className="font-medium text-orange-800">{formatCurrency(currentTransaction.transaction_charge)}</span>
                  </div>
                )}
                <div className="flex justify-between py-2">
                  <span className="text-stone-500">Date</span>
                  <span className="font-medium text-stone-900">
                    {new Date(currentTransaction.date).toLocaleString('en-US', {
                      year: 'numeric',
                      month: 'short',
                      day: 'numeric',
                      hour: '2-digit',
                      minute: '2-digit'
                    })}
                  </span>
                </div>
              </div>
              
              <div className="flex gap-2 pt-2">
                <Button
                  onClick={() => { setIsDetailOpen(false); openEdit(currentTransaction); }}
                  variant="outline"
                  className="flex-1 rounded-full"
                >
                  <Edit className="w-4 h-4 mr-2" />
                  Edit
                </Button>
                <Button
                  onClick={() => { setIsDetailOpen(false); openDeleteConfirm(currentTransaction); }}
                  variant="outline"
                  className="flex-1 rounded-full text-orange-800 hover:bg-orange-50"
                >
                  <Trash2 className="w-4 h-4 mr-2" />
                  Delete
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
      
      {/* Edit Dialog */}
      <Dialog open={isEditOpen} onOpenChange={setIsEditOpen}>
        <DialogContent data-testid="edit-transaction-dialog">
          <DialogHeader>
            <DialogTitle>Edit Transaction</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleEdit} className="space-y-4">
            {currentTransaction?.type !== 'transfer' && (
              <>
                <div>
                  <Label>Amount (TZS)</Label>
                  <Input
                    type="tel"
                    data-testid="edit-amount-input"
                    value={editAmount.displayValue}
                    onChange={editAmount.handleChange}
                    placeholder="0"
                    className="bg-stone-50 text-xl h-12 font-semibold text-center"
                    required
                  />
                  <p className="text-xs text-stone-500 mt-1 text-center">
                    {editAmount.numericValue ? `TZS ${editAmount.displayValue}` : 'Enter amount'}
                  </p>
                </div>
                <div>
                  <Label>Wallet</Label>
                  <Select value={editData.wallet_id} onValueChange={(value) => setEditData({ ...editData, wallet_id: value })}>
                    <SelectTrigger className="bg-stone-50" data-testid="edit-wallet-select">
                      <SelectValue placeholder="Select wallet" />
                    </SelectTrigger>
                    <SelectContent>
                      {wallets.map(w => (
                        <SelectItem key={w.id} value={w.id}>{w.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </>
            )}
            {currentTransaction?.type === 'cash_in' ? (
              <div>
                <Label>Source</Label>
                <Input
                  data-testid="edit-source-input"
                  value={editData.source}
                  onChange={(e) => setEditData({ ...editData, source: e.target.value })}
                  className="bg-stone-50"
                  required
                />
              </div>
            ) : currentTransaction?.type === 'cash_out' ? (
              <div>
                <Label>Category</Label>
                <CategoryCombobox
                  categories={sortedCategories}
                  value={editData.category_id}
                  onChange={(value) => setEditData({ ...editData, category_id: value })}
                  testId="edit-category-select"
                />
              </div>
            ) : (
              <div className="bg-stone-50 border border-stone-200 rounded-lg p-3 text-sm">
                <p className="text-stone-500">Transfer</p>
                <p className="font-medium text-stone-900">{currentTransaction?.wallet_name} to {currentTransaction?.to_wallet_name || 'another wallet'}</p>
                <p className="text-stone-600 mt-1">{formatCurrency(currentTransaction?.amount || 0)}</p>
              </div>
            )}
            <div>
              <Label>Date</Label>
              <Input
                type="date"
                data-testid="edit-date-input"
                value={editData.date || ''}
                onChange={(e) => setEditData({ ...editData, date: e.target.value })}
                className="bg-stone-50"
                max={new Date().toISOString().split('T')[0]}
              />
            </div>
            <div>
              <Label>Narration</Label>
              <Input
                data-testid="edit-narration-input"
                value={editData.narration}
                onChange={(e) => setEditData({ ...editData, narration: e.target.value })}
                className="bg-stone-50"
              />
            </div>
            {/* Check if backdated and show reason field */}
            {currentTransaction && new Date(currentTransaction.date) < new Date(new Date().setHours(0, 0, 0, 0)) && (
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                <Label className="text-amber-800">Reason for Edit (Required for backdated transactions)</Label>
                <Input
                  data-testid="edit-reason-input"
                  value={editData.reason || ''}
                  onChange={(e) => setEditData({ ...editData, reason: e.target.value })}
                  className="bg-white mt-1"
                  placeholder="Why are you editing this transaction?"
                  required
                />
              </div>
            )}
            <Button type="submit" disabled={saving.edit} className="w-full bg-emerald-900 hover:bg-emerald-800 text-white rounded-full" data-testid="edit-transaction-submit-button">
              {saving.edit ? 'Saving...' : 'Update Transaction'}
            </Button>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteConfirm.show} onOpenChange={(open) => !open && setDeleteConfirm({ show: false, transaction: null, reason: '' })}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="text-red-600">Delete Transaction</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <p className="text-stone-600">
              Are you sure you want to delete this transaction? This action cannot be undone.
            </p>
            {deleteConfirm.transaction && (
              <div className="bg-stone-50 rounded-lg p-3 text-sm">
                <p className="font-medium">
                  {deleteConfirm.transaction.type === 'cash_in' ? 'Cash In' : 'Cash Out'}: {formatCurrency(deleteConfirm.transaction.amount)}
                </p>
                <p className="text-stone-500">
                  {deleteConfirm.transaction.wallet_name} • {new Date(deleteConfirm.transaction.date).toLocaleDateString()}
                </p>
              </div>
            )}
            <div>
              <Label className="text-stone-700">Reason for Deletion *</Label>
              <Input
                data-testid="delete-reason-input"
                value={deleteConfirm.reason}
                onChange={(e) => setDeleteConfirm({ ...deleteConfirm, reason: e.target.value })}
                placeholder="Why are you deleting this transaction?"
                className="mt-1"
                required
              />
            </div>
            <div className="flex gap-3 pt-2">
              <Button
                variant="outline"
                onClick={() => setDeleteConfirm({ show: false, transaction: null, reason: '' })}
                className="flex-1"
              >
                Cancel
              </Button>
              <Button
                onClick={handleDelete}
                disabled={saving.delete}
                className="flex-1 bg-red-600 hover:bg-red-700 text-white"
                data-testid="confirm-delete-transaction-btn"
              >
                {saving.delete ? 'Deleting...' : 'Delete'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
};

const TransactionList = ({ transactions, onEdit, onDelete, onView }) => {
  if (transactions.length === 0) {
    return (
      <div className="bg-white border border-stone-200 rounded-xl p-12 text-center">
        <p className="text-stone-600">No transactions found</p>
      </div>
    );
  }

  return (
    <div className="bg-white border border-stone-200 rounded-xl overflow-hidden">
      <div className="divide-y divide-stone-100">
        {transactions.map((transaction, index) => (
          <motion.div
            key={transaction.id}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: index * 0.05 }}
            className="p-3 sm:p-6 hover:bg-stone-50 transition-colors cursor-pointer"
            data-testid={`transaction-${transaction.id}`}
            onClick={() => onView(transaction)}
          >
            {/* Mobile Layout */}
            <div className="block sm:hidden space-y-2">
              <div className="flex items-start justify-between">
                <div className="flex items-start space-x-2 flex-1 min-w-0">
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${
                    transaction.type === 'cash_in' ? 'bg-emerald-50' : transaction.type === 'transfer' ? 'bg-stone-100' : 'bg-orange-50'
                  }`}>
                    {transaction.type === 'cash_in' ? (
                      <ArrowUpRight className="w-5 h-5 text-emerald-700" strokeWidth={1.5} />
                    ) : transaction.type === 'transfer' ? (
                      <ArrowLeftRight className="w-5 h-5 text-stone-700" strokeWidth={1.5} />
                    ) : (
                      <ArrowDownRight className="w-5 h-5 text-orange-700" strokeWidth={1.5} />
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold text-sm text-stone-900 truncate">
                      {transaction.type === 'cash_in' ? transaction.source : transaction.type === 'transfer' ? `Transfer to ${transaction.to_wallet_name || 'wallet'}` : transaction.category_name}
                    </p>
                    <p className="text-xs text-stone-500 truncate">{transaction.wallet_name}</p>
                  </div>
                </div>
                <div className="text-right flex-shrink-0 ml-2">
                  <p className={`text-base font-bold whitespace-nowrap ${
                    transaction.type === 'cash_in' ? 'text-emerald-900' : transaction.type === 'transfer' ? 'text-stone-900' : 'text-orange-800'
                  }`}>
                    {transaction.type === 'cash_in' ? '+' : transaction.type === 'transfer' ? '' : '-'}{formatCurrency(transaction.amount).replace('TZS ', '')}
                  </p>
                </div>
              </div>
              <div className="flex items-center justify-between text-xs text-stone-500 pl-12">
                <p className="truncate">
                  {new Date(transaction.date).toLocaleDateString('en-US', {
                    month: 'short',
                    day: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit'
                  })}
                </p>
                <div className="flex gap-1 flex-shrink-0 ml-2" onClick={(e) => e.stopPropagation()}>
                  <button
                    onClick={() => onEdit(transaction)}
                    className="p-1.5 hover:bg-emerald-50 rounded text-emerald-900"
                    data-testid={`edit-transaction-mobile-${transaction.id}`}
                  >
                    <Edit className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => onDelete(transaction)}
                    className="p-1.5 hover:bg-orange-50 rounded text-orange-800"
                    data-testid={`delete-transaction-mobile-${transaction.id}`}
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>

            {/* Desktop Layout */}
            <div className="hidden sm:flex items-center justify-between">
              <div className="flex items-center space-x-4 flex-1">
                <div className={`w-12 h-12 rounded-full flex items-center justify-center ${
                  transaction.type === 'cash_in' ? 'bg-emerald-50' : transaction.type === 'transfer' ? 'bg-stone-100' : 'bg-orange-50'
                }`}>
                  {transaction.type === 'cash_in' ? (
                    <ArrowUpRight className="w-6 h-6 text-emerald-700" strokeWidth={1.5} />
                  ) : transaction.type === 'transfer' ? (
                    <ArrowLeftRight className="w-6 h-6 text-stone-700" strokeWidth={1.5} />
                  ) : (
                    <ArrowDownRight className="w-6 h-6 text-orange-700" strokeWidth={1.5} />
                  )}
                </div>
                <div className="flex-1">
                  <p className="font-semibold text-stone-900">
                    {transaction.type === 'cash_in' ? transaction.source : transaction.type === 'transfer' ? `Transfer to ${transaction.to_wallet_name || 'wallet'}` : transaction.category_name}
                  </p>
                  <div className="flex items-center gap-4 mt-1">
                    <p className="text-sm text-stone-500">{transaction.wallet_name}</p>
                    {transaction.phone_number && (
                      <p className="text-sm text-stone-500">{transaction.phone_number}</p>
                    )}
                  </div>
                  {transaction.narration && (
                    <p className="text-sm text-stone-600 mt-1">{transaction.narration}</p>
                  )}
                  {transaction.transaction_charge > 0 && (
                    <p className="text-xs text-orange-600 mt-1">Charge: {formatCurrency(transaction.transaction_charge)}</p>
                  )}
                </div>
              </div>
              <div className="text-right flex items-center gap-4">
                <div>
                  <p className={`text-xl font-bold ${
                    transaction.type === 'cash_in' ? 'text-emerald-900' : transaction.type === 'transfer' ? 'text-stone-900' : 'text-orange-800'
                  }`}>
                    {transaction.type === 'cash_in' ? '+' : transaction.type === 'transfer' ? '' : '-'}{formatCurrency(transaction.amount).replace('TZS ', '')}
                  </p>
                  <p className="text-sm text-stone-500 mt-1">
                    {new Date(transaction.date).toLocaleDateString('en-US', {
                      year: 'numeric',
                      month: 'short',
                      day: 'numeric',
                      hour: '2-digit',
                      minute: '2-digit'
                    })}
                  </p>
                </div>
                <div className="flex gap-2" onClick={(e) => e.stopPropagation()}>
                  <Button
                    onClick={() => onEdit(transaction)}
                    variant="ghost"
                    size="sm"
                    className="text-emerald-900 hover:bg-emerald-50"
                    data-testid={`edit-transaction-${transaction.id}`}
                  >
                    <Edit className="w-4 h-4" />
                  </Button>
                  <Button
                    onClick={() => onDelete(transaction)}
                    variant="ghost"
                    size="sm"
                    className="text-orange-800 hover:bg-orange-50"
                    data-testid={`delete-transaction-${transaction.id}`}
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            </div>
          </motion.div>
        ))}
      </div>
    </div>
  );
};
